import { useSyncExternalStore } from "react";
import { useNow } from "use-intl";

const MINUTE_MS = 60_000;

let current = new Date();
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function tick() {
  current = new Date();
  for (const listener of listeners) listener();
}

function subscribeMinuteClock(listener: () => void) {
  listeners.add(listener);
  timer ??= setInterval(tick, MINUTE_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0 || !timer) return;
    clearInterval(timer);
    timer = null;
  };
}

function subscribeNever() {
  return () => {};
}

/** Current minute-resolution time; refreshes lazily when read after the clock was idle. */
export function readMinuteClock(): Date {
  if (Math.abs(Date.now() - current.getTime()) >= MINUTE_MS) current = new Date();
  return current;
}

/**
 * Wall-clock `now` shared by every live relative time through one interval.
 * The server render and hydration use the IntlProvider `now` so markup matches;
 * client renders afterwards read the real time and re-render once a minute.
 * With `live` off it returns the provider `now` and never re-renders.
 */
export function useMinuteNow(live: boolean): Date {
  const providerNow = useNow();
  return useSyncExternalStore(
    live ? subscribeMinuteClock : subscribeNever,
    live ? readMinuteClock : () => providerNow,
    () => providerNow,
  );
}
